import { Button, Card, Col, Collapse, Form, Row, Select, Space } from "antd";
import addreses from "config/api/addresses";
import useApi from "hooks/useApi";
import { useTranslation } from "react-i18next"
import Page from "../shared/Page";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import util from "config/utils";
import { Gateway } from "models/gateway";
import AjaxSelect from "../shared/AjaxSelect";
import CustomDatePicker from "../shared/CustomDatePicker";
import { PagedData, PaymentAggrigationReportItem, PaymentStateType } from "models";
import ChartContainer from "../shared/ChartContainer";
import colors from "config/colors";
import config from "config";
import CustomHighstock from "components/panel/shared/CustomHighstock";
import AtomTheme from "recoil/atoms/AtomTheme";
import { useRecoilValue } from "recoil";
import AdminPageContext from "context/AdminPageContext";
import useGroupByData from "hooks/useGetChartData";

const { Panel } = Collapse;
const Report = () => {
    const { t } = useTranslation();
    const cht = useRef<CustomHighstock.StockChart>()
    const [filterFrm] = Form.useForm();
    const theme = useRecoilValue(AtomTheme);
    const [collapsed, toggleCollapsed] = useState(true);
    const [terminalId, setTerminalId] = useState<string | null>(null);
    const [http, downloading] = useApi();
    const from = useMemo(() => {
        let dt = new Date();
        dt.setDate(dt.getDate() - 7);
        return dt;
    }, []);
    const convert = useGroupByData({
        valueProps: "amount",
        dateTimeProps: "insertDateMi",
        groupByProps: "insertDate"
    })
    const [history, loading] = useApi<PagedData<PaymentAggrigationReportItem>>({
        autoCallUrl: `${addreses.reportPaymentAggregation}?pageNumber=1&pageSize=100&state=${PaymentStateType.paymentConfirmed}&fromDate=${from.toISOString()}`,
        onSuccess: (res) => {
            let chartData = convert({
                data: res?.items
            });
            cht.current = CustomHighstock.stockChart({
                chart: {
                    height: 350,
                    renderTo: "trans-chart",
                    style: {
                        fontFamily: "Vazir",
                    },
                    spacingRight: 0,
                    marginLeft: 50
                },
                plotOptions: {
                    series: {
                        marker: {
                            fillColor: colors.purple,
                        }
                    }
                },
                tooltip: {
                    enabled: true,
                    //crosshair: true,
                    useHTML: true,
                    shadow: false,
                    borderWidth: 0,
                    backgroundColor: "transparent",
                    formatter: function () {
                        const data: any = this.points?.[0];
                        if (!data) return "";
                        let items: any[] = [];
                        for (let p of this.points ?? []) {
                            items.push({
                                name: p.series.name,
                                value: p.y,
                                dir: "ltr",
                                color: p.color,
                                prefix: t("baseCurrency")
                            })
                        }
                        const first = res?.items[0];
                        const last = res?.items[res.items.length - 1];
                        if (!first || !last) return "";
                        let range = new Date(last.insertDateMi).getTime() - new Date(first.insertDateMi).getTime();
                        if (range < 30 * 24 * 60 * 1000)
                            return util.createTooltip(items, theme, CustomHighstock.dateFormat("%Y/%m/%d %H:%M", this.x as number))
                        else
                            return util.createTooltip(items, theme, CustomHighstock.dateFormat("%Y/%m/%d", this.x as number))
                    },
                },
                title: {
                    text: t("income") ?? "",
                    align: "right",
                    style: {
                        fontWeight: "700",
                        fontSize: "14"
                    }
                },
                credits: {
                    enabled: false,
                },
                navigator: {
                    enabled: false
                },
                rangeSelector: {
                    enabled: false,
                },
                scrollbar: {
                    enabled: false,
                },
                legend: {
                    enabled: true,
                    align: "left",
                    verticalAlign: "top",
                    y: -40,
                    x: 20,
                    rtl: true,
                    useHTML: true,
                    symbolWidth: 0,
                    symbolHeight: 0,
                    symbolRadius: 0,
                    squareSymbol: false,
                    labelFormatter: function () {
                        return util.createLegend(this.name, this.color?.toString() ?? "", "square");
                    }
                },
                xAxis: {
                    type: "datetime",
                    lineWidth: 2,
                    lineColor: colors[theme].border,
                    tickColor: colors[theme].border
                },
                yAxis: [
                    {
                        gridLineWidth: 0,
                        lineWidth: 0,
                        labels: {
                            formatter() {
                                return this.value !== null
                                    ? util.formatNumber(this.value as number)
                                    : this.value;
                            },
                        },
                        lineColor: "#F3F5F9",
                        opposite: false,
                    },
                ],
                series: [
                    {
                        type: "column",
                        name: t("amount") ?? "",
                        color: colors.green,
                        dataGrouping: {
                            enabled: false,
                        },
                        data: chartData,
                    },
                ],
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px'
                    }
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: config.breakpoints.xs
                        },
                        chartOptions: {
                            chart: {
                                height: 300,
                                marginTop: 150
                            },

                            legend: {
                                y: 0,
                                align: "left",
                                itemMarginTop: 10,
                            }
                        }
                    }]
                }
            });
        }
    })
    const onFilter = (data: any) => {
        history.get(`${addreses.reportPaymentAggregation}?${util.convertToQueryString({
            ...data,
            fromDate: data.fromDate ? new Date(data.fromDate).toISOString() : null,
            toDate: data.toDate ? new Date(data.toDate).toISOString() : null,
            pageNumer: 1,
            pageSize: 100
        })}`);
    }
    const handleDownload = (e: MouseEvent<HTMLElement>) => {
        const data = filterFrm.getFieldsValue();
        http.download(addreses.reportDownloadPaymentAggregation, {
            ...data,
            fromDate: data.fromDate ? new Date(data.fromDate).toISOString() : null,
            toDate: data.toDate ? new Date(data.toDate).toISOString() : null,
        })
    }
    useEffect(() => {
        cht.current?.reflow();
    }, [collapsed])
    return <Page id="report" title="report" className="page">
        <Space direction="vertical" size={"large"}>
            <Collapse
                defaultActiveKey={['filters']}
                onChange={() => toggleCollapsed(s => !s)}
            >
                <Panel
                    header={t("filters")}
                    key="filters">
                    <Form
                        name="filters"
                        form={filterFrm}
                        initialValues={{
                            fromDate: from,
                            state: PaymentStateType.paymentConfirmed
                        }}
                        onFinish={onFilter}
                        autoComplete="off"
                    >
                        <AdminPageContext.Provider value={{ form: filterFrm }}>
                            <Row gutter={[20, 20]}>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="gatewayId" label={t("gateway")}>
                                        <Select
                                            placeholder={t("gateway")}
                                            allowClear
                                            options={util.convrtEnumToOptions(Gateway)}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="terminalId">
                                        <AjaxSelect
                                            init
                                            fill={false}
                                            name="terminalId"
                                            label={t("terminal") ?? ""}
                                            placeholder={t("terminal") ?? ""}
                                            apiUrl={addreses.terminalDropDown}
                                            allowClear={true}
                                            afterChange={(id) => setTerminalId(id)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="applicationId">
                                        <AjaxSelect
                                            init
                                            fill={false}
                                            name="applicationId"
                                            label={t("application") ?? ""}
                                            placeholder={t("application") ?? ""}
                                            apiUrl={addreses.applicationDropDown}
                                            allowClear={true}
                                            params={{ terminalId }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="state" label={t("state")}>
                                        <Select
                                            placeholder={t("state")}
                                            allowClear
                                            options={util.convrtEnumToOptions(PaymentStateType)}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item
                                        name="fromDate"
                                        label={t("fromDate")}
                                    >
                                        <CustomDatePicker placeholder={t("fromDate") ?? ""} />
                                    </Form.Item>

                                </Col>
                                <Col xs={24} sm={8}>
                                    <Form.Item
                                        name="toDate"
                                        label={t("toDate")}
                                    >
                                        <CustomDatePicker endTime placeholder={t("toDate") ?? ""} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item className="flex-row-end btns">
                                <Button type="primary"
                                    loading={downloading}
                                    htmlType="button"
                                    style={{ backgroundColor: "green", marginLeft: 16 }}
                                    onClick={handleDownload}>
                                    {t("download")}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t("filter")}
                                </Button>
                            </Form.Item>
                        </AdminPageContext.Provider>
                    </Form>
                </Panel>
            </Collapse>
            <Card>
                <ChartContainer
                    height='350px'
                    id="trans-chart"
                    loading={loading}
                />
            </Card>
        </Space>
    </Page>;
};
export default Report;